/**
 * EmailAuthForm — default email + password sign-in for Trivly.
 * Single component toggles between "Sign in" and "Create account" modes.
 * Uses the same session_token httpOnly cookie as Google Auth so all
 * downstream /api/user/* routes work identically.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User as UserIcon, Loader2, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { confirm as hapticConfirm, error as hapticError } from '@/lib/haptics';
import { toast } from 'sonner';

export default function EmailAuthForm() {
  const { loginWithEmail, registerWithEmail } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const isSignup = mode === 'signup';
  const canSubmit =
    email.trim().length > 3 &&
    password.length >= (isSignup ? 8 : 1) &&
    (!isSignup || name.trim().length > 0) &&
    !busy;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    try {
      if (isSignup) {
        await registerWithEmail(email.trim(), password, name.trim());
        hapticConfirm();
        toast.success('Welcome to Trivly');
      } else {
        await loginWithEmail(email.trim(), password);
        hapticConfirm();
        toast.success('Signed in');
      }
    } catch (err) {
      hapticError();
      toast.error(String(err?.message || 'Auth failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="email-auth-form"
      className="glass-card p-4 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#15291C]">
            {isSignup ? 'Create your account' : 'Sign in to Trivly'}
          </p>
          <p className="text-[12px] text-[#7A8A80] mt-0.5">
            {isSignup ? 'Sync favorites & diary across all your devices' : 'Welcome back'}
          </p>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isSignup && (
          <motion.div
            key="name-field"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="flex items-center gap-2 rounded-2xl bg-white border border-[#0F1A13]/10 focus-within:border-[#15291C] px-3 py-2.5">
              <UserIcon className="w-4 h-4 text-[#15291C]" />
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                maxLength={80}
                data-testid="email-auth-input-name"
                className="flex-1 bg-transparent outline-none text-[15px] text-[#0F1A13] placeholder:text-[#A8B5AB]"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center gap-2 rounded-2xl bg-white border border-[#0F1A13]/10 focus-within:border-[#15291C] px-3 py-2.5">
        <Mail className="w-4 h-4 text-[#15291C]" />
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          inputMode="email"
          data-testid="email-auth-input-email"
          className="flex-1 bg-transparent outline-none text-[15px] text-[#0F1A13] placeholder:text-[#A8B5AB]"
        />
      </div>

      <div className="flex items-center gap-2 rounded-2xl bg-white border border-[#0F1A13]/10 focus-within:border-[#15291C] px-3 py-2.5">
        <Lock className="w-4 h-4 text-[#15291C]" />
        <input
          type={showPw ? 'text' : 'password'}
          placeholder={isSignup ? 'At least 8 characters' : 'Your password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={isSignup ? 'new-password' : 'current-password'}
          minLength={isSignup ? 8 : undefined}
          maxLength={128}
          data-testid="email-auth-input-password"
          className="flex-1 bg-transparent outline-none text-[15px] text-[#0F1A13] placeholder:text-[#A8B5AB]"
        />
        <button
          type="button"
          onClick={() => setShowPw((v) => !v)}
          aria-label={showPw ? 'Hide password' : 'Show password'}
          data-testid="email-auth-toggle-password"
          className="text-[#7A8A80] hover:text-[#15291C]"
        >
          {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        data-testid="email-auth-submit"
        className="mt-1 gt-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        {isSignup ? 'Create account' : 'Sign in'}
      </button>

      <div className="text-center text-[12px] text-[#4A5A50]">
        {isSignup ? 'Already have an account? ' : 'New to Trivly? '}
        <button
          type="button"
          onClick={() => setMode(isSignup ? 'signin' : 'signup')}
          data-testid="email-auth-toggle-mode"
          className="text-[#15291C] font-semibold underline underline-offset-2 hover:text-[#0F1A13]"
        >
          {isSignup ? 'Sign in' : 'Create one'}
        </button>
      </div>
    </form>
  );
}
